// Google Ads per-account picker — pure helpers shared by the server module, the
// callback page, and the account API-Connections picker. No I/O.
//
// WHY A PICKER: Zernio's connectAds(platform:'googleads') with NO adAccountId
// auto-binds the FIRST Google Ads customer the agency token can see, with no
// selection step — so it grabbed an unrelated NZD account for an AU club. The fix
// (probe-confirmed: the token enumerates the whole 477-account book via
// listAdAccounts, and the correct account is reachable) is kyck's own picker:
// connectAds (OAuth once) → listAdAccounts (enumerate) → search/pick → connectAds
// again with adAccountId=<chosen bare customer id> to SCOPE the connection.
//
// Google Ads is a STANDALONE OAuth in Zernio (no agency/manager posting model,
// like Instagram) — but unlike IG, the connection covers EVERY customer the token
// sees, so the picker is what makes it usable. The same picker serves first-connect
// AND re-pick (correcting a wrong binding: no re-OAuth, just enumerate→pick→bind).

// One row of the enumerated Google Ads accounts list. `id` is the Google Ads
// customer id (bare digits). Rows also carry currency + status + an optional
// manager (MCC) customer id — shown in the picker so an AU-vs-NZ mismatch is
// obvious at selection time.
export interface GoogleAdsAccountOption {
  id: string; // bare Google Ads customer id (digits)
  name: string;
  currency: string | null;
  status: string | null;
  managerCustomerId: string | null;
}

// The list endpoint's response. The list rides Zernio's SHARED Google Ads
// developer-token quota (it 429s and recovers ~18h later), so it's cached
// server-side and degrades gracefully:
//   - quotaLimited: a shared-quota 429 was hit on this call.
//   - stale: the accounts are from a cache older than the TTL (served because a
//     refresh 429'd) — still useful, flagged in the UI.
//   - available: false ONLY when quotaLimited AND there was no cache to fall back
//     on → the picker shows "temporarily unavailable, retry shortly", never a
//     silent empty list.
export interface GoogleAdsAccountListResult {
  accounts: GoogleAdsAccountOption[];
  fetchedAt: string | null; // ISO of when the list was fetched from Zernio
  boundCustomerId: string | null; // the account's current googleAdsCustomerId (highlight in the picker)
  total: number;
  quotaLimited: boolean;
  stale: boolean;
  available: boolean;
}

// The result of binding a chosen customer to the account.
export interface GoogleAdsSelectResult {
  bound: true;
  customerId: string; // bare digits, as persisted to Account.googleAdsCustomerId
  name: string | null;
  currency: string | null;
}

// The CURRENT bound Google Ads customer for a connected `googleads` SocialAccount,
// read from its Zernio metadata bag.
//
// ⚠️ After a re-scope (our "Change" → connectAds adAccountId=…), the live customer
// is `metadata.scopedAdAccountIds[0]` — NOT `metadata.googleAdsCustomerIds[0]`,
// which stays STALE at the OAuth-time auto-pick. Observed live: after re-scoping
// Cromer to the AU account, `googleAdsCustomerIds:["5116692401"]` (the NZD
// auto-pick) was UNCHANGED while `scopedAdAccountIds:["9519678088"]` reflected the
// re-scope. So prefer the scope; fall back to the auto-pick for a never-re-scoped
// connection. Pure. (googleads reuses the same `scopedAdAccountIds` metadata field
// Meta uses for its act_ scope — call this ONLY on a googleads row's metadata.)
export function resolveBoundGoogleAdsCustomerId(
  metadata: Record<string, unknown> | null | undefined,
): string | null {
  const meta = (metadata ?? {}) as Record<string, unknown>;
  const firstString = (v: unknown): string | null => {
    if (!Array.isArray(v)) return null;
    for (const x of v) {
      if (typeof x === 'string' && x.trim() !== '') return x.trim();
    }
    return null;
  };
  return firstString(meta.scopedAdAccountIds) ?? firstString(meta.googleAdsCustomerIds);
}

// Normalize a Google Ads customer id to bare digits. Google shows customer ids as
// `123-456-7890`; the API wants bare digits (`1234567890`). listAdAccounts returns
// the bare id already, but a hand-typed / pasted value may carry dashes/spaces, so
// we strip every non-digit. Throws on an empty result — a customer id is required
// to scope the connection, so a blank value is a hard error, not a silent bind of
// everything.
export function normalizeGoogleAdsCustomerId(raw: string): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits === '') {
    throw new Error('A Google Ads customer id is required.');
  }
  return digits;
}

// Build the connectAds re-scope args for a chosen customer. `adAccountId` scopes
// the googleads connection to exactly this customer (Zernio: "googleads = bare
// customer id digits … setting a scope also removes already-synced ads from
// de-scoped ad accounts"). Pure so the exact wire arg is unit-tested + loggable.
export function buildGoogleAdsSelectScope(customerId: string): { adAccountId: string } {
  return { adAccountId: normalizeGoogleAdsCustomerId(customerId) };
}

// ---------- OAuth callback param parsing ----------

export interface GoogleAdsCallbackParams {
  kyckAccountId: string | null; // OUR kyck account ref (rides ?kyckAccountId, distinct from Zernio's accountId)
  connected: string | null; // Zernio's `connected` flag (should be 'googleads')
  googleAccountId: string | null; // Zernio's appended accountId = the googleads SocialAccount id (display only)
  error: string | null; // present when the OAuth failed / was cancelled
}

function nonEmpty(v: string | null): string | null {
  return v && v.trim() !== '' ? v.trim() : null;
}

export function parseGoogleAdsCallbackParams(params: URLSearchParams): GoogleAdsCallbackParams {
  return {
    kyckAccountId: nonEmpty(params.get('kyckAccountId')),
    connected: nonEmpty(params.get('connected')),
    googleAccountId: nonEmpty(params.get('accountId')),
    error: nonEmpty(params.get('error')),
  };
}

export type GoogleAdsCallbackOutcome =
  | 'ready' // we have the kyck account ref — open the picker (enumerate → pick → bind)
  | 'missing-account' // no kyck account reference — the flow can't be resolved
  | 'error'; // Zernio returned an OAuth error

export function classifyGoogleAdsCallback(p: GoogleAdsCallbackParams): GoogleAdsCallbackOutcome {
  if (p.error) return 'error';
  if (!p.kyckAccountId) return 'missing-account';
  return 'ready';
}

// Which affordance to show on the account's API-Connections Google Ads row:
//   - 'none'    — no Zernio profile, or status unknown (nothing actionable yet)
//   - 'connect' — profile exists, not connected → OAuth then pick
//   - 'change'  — already connected → re-pick (no OAuth), to correct a binding
// Unlike Instagram (connect-only), Google Ads offers 'change' when connected so a
// wrongly-bound account (the NZD-for-an-AU-club bug) can be corrected in place.
export function googleAdsConnectAffordance(input: {
  hasProfile: boolean;
  statusUnknown: boolean;
  connected: boolean | null;
}): 'none' | 'connect' | 'change' {
  if (!input.hasProfile || input.statusUnknown) return 'none';
  if (input.connected === true) return 'change';
  if (input.connected === false) return 'connect';
  return 'none';
}

// Decide how to serve the accounts list when the live Zernio fetch FAILED (the
// fresh-cache short-circuit happens before this). The list rides a SHARED quota,
// so a 429 must degrade gracefully rather than break the picker:
//   - quota 429 + a stale cache  → serve the stale cache (flagged), still useful.
//   - quota 429 + NO cache       → unavailable (the UI shows "retry shortly"),
//                                   never a silent empty picker.
//   - a non-quota error          → neither: the caller rethrows the real error.
// Pure + total so the degrade behaviour is unit-tested without the SDK.
export function decideGoogleAdsListFallback(input: {
  quotaExhausted: boolean;
  hasStaleCache: boolean;
}): { serveStale: boolean; unavailable: boolean } {
  if (!input.quotaExhausted) return { serveStale: false, unavailable: false };
  return input.hasStaleCache
    ? { serveStale: true, unavailable: false }
    : { serveStale: false, unavailable: true };
}
